import { FC, useState, useEffect, ReactElement, useMemo } from "react";
import {
  Button,
  ButtonVariant,
  ButtonSize,
  Input,
  InputSize
} from "shared/components";

interface ZipCodeRangeCardProps {
  options: string[];
  onAddRange?: (range: { from: string; through: string }) => void;
  usedZipCodes?: string[];
  segmentName?: string;
  optionsWithSegmentMap?: Record<string, string>;
  onClose?: () => void;
  onSelect?: (zipCodes: string[]) => void;
  selectedOptions: string[];
}

export const ZipCodeRangeCard: FC<ZipCodeRangeCardProps> = ({
  options,
  usedZipCodes = [],
  segmentName = "",
  optionsWithSegmentMap = {},
  onClose,
  onSelect,
  selectedOptions
}): ReactElement => {
  const [from, setFrom] = useState("");
  const [through, setThrough] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [touched, setTouched] = useState({
    from: false,
    through: false
  });
  const [error, setError] = useState("");

  const numericOptions = useMemo(() => {
    const optionsMap = new Map<number, string>();
    options.forEach((option) => {
      optionsMap.set(parseInt(option, 10), option);
    });
    return optionsMap;
  }, [options]);

  const usedZipCodesSet = useMemo(() => new Set(usedZipCodes), [usedZipCodes]);

  useEffect(() => {
    if (touched.from || touched.through) validateRange();
  }, [from, through, touched]);

  const validateRange = (): boolean => {
    if (!from && !through) {
      setError("");
      return false;
    }

    if (!from || !through) {
      setError("Both fields are required");
      return false;
    }

    if (!/^\d{5}$/.test(from) || !/^\d{5}$/.test(through)) {
      setError("ZIP codes must be 5 digits");
      return false;
    }

    const fromNum = parseInt(from, 10);
    const throughNum = parseInt(through, 10);

    if (isNaN(fromNum) || isNaN(throughNum)) {
      setError("ZIP codes must be valid numbers");
      return false;
    }

    if (throughNum < fromNum) {
      setError("'Through' value must be greater than or equal to 'From'");
      return false;
    }

    for (let i = fromNum; i <= throughNum; i++) {
      const zipCode = i.toString().padStart(5, "0");
      if (usedZipCodesSet.has(zipCode)) {
        const segmentNameFromMap =
          optionsWithSegmentMap[zipCode] || segmentName;
        setError(
          `You're to add ZIPs that are used in the "${segmentNameFromMap}" group`
        );
        return false;
      }
    }

    setError("");
    return true;
  };

  const handleAddRange = async () => {
    setIsLoading(true);
    setTouched({ from: true, through: true });

    if (!validateRange()) {
      setIsLoading(false);
      return;
    }

    const fromNum = parseInt(from, 10);
    const throughNum = parseInt(through, 10);

    const zipCodes: string[] = [];

    for (let i = fromNum; i <= throughNum; i++) {
      const originalZip = numericOptions.get(i);
      if (originalZip) zipCodes.push(originalZip);
    }

    if (zipCodes.length === 0) {
      setTouched({ from: false, through: false });
      setError("No valid ZIP codes found in the range");
      setIsLoading(false);
      return;
    }

    const newOptionsSet = new Set([...selectedOptions, ...zipCodes]);

    const newOptions = Array.from(newOptionsSet).sort(
      (a, b) => parseInt(a, 10) - parseInt(b, 10)
    );

    onSelect?.(newOptions);

    setFrom("");
    setThrough("");
    setError("");

    onClose?.();

    setIsLoading(false);
  };

  const handleFromChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (/^\d*$/.test(value) && value.length <= 5) setFrom(value);
  };

  const handleThroughChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTouched({ from: true, through: true });
    const value = e.target.value;
    if (/^\d*$/.test(value) && value.length <= 5) setThrough(value);
  };

  const handleBlur = (field: "from" | "through") => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  return (
    <div className="animate-bounce-in border border-alpha-100 rounded-lg bg-white shadow-xl w-full">
      <div className="p-4 border-b border-alpha-100">
        <h3 className="text-base font-medium">Add ZIP code range</h3>
        <p className="text-sm text-content-400 mt-1">
          Enter a range of ZIP codes to add them all at once.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4 p-4 border-b border-alpha-100">
        <div className="space-y-1">
          <Input
            id="zip-from"
            label="From"
            size={InputSize.SM}
            value={from}
            onChange={handleFromChange}
            onBlur={() => handleBlur("from")}
            placeholder="78701"
            className="w-full"
            type="text"
            noIntlNormalization
            error={touched.from && (!from || from.length !== 5)}
            required
          />
          {touched.from && !from && (
            <p className="text-xs text-danger-500 mt-1">ZIP code is required</p>
          )}
          {touched.from && from && from.length !== 5 && (
            <p className="text-xs text-danger-500 mt-1">Must be 5 digits</p>
          )}
        </div>
        <div className="space-y-1">
          <Input
            id="zip-through"
            label="Through"
            size={InputSize.SM}
            value={through}
            onChange={handleThroughChange}
            onBlur={() => handleBlur("through")}
            placeholder="78703"
            className="w-full"
            type="text"
            noIntlNormalization
            error={touched.through && (!through || through.length !== 5)}
            required
          />
          {touched.through && !through && (
            <p className="text-xs text-danger-500 mt-1">ZIP code is required</p>
          )}
          {touched.through && through && through.length !== 5 && (
            <p className="text-xs text-danger-500 mt-1">Must be 5 digits</p>
          )}
        </div>
      </div>

      {error && (
        <p className="text-danger-600 font-medium text-sm p-4 border-b border-alpha-100">
          {error}
        </p>
      )}

      <div className="flex justify-end p-4">
        <Button
          className="w-[90px]"
          variant={ButtonVariant.PRIMARY}
          size={ButtonSize.SM}
          loading={isLoading}
          onClick={handleAddRange}
          disabled={!from || !through || !!error || isLoading}
        >
          Add
        </Button>
      </div>
    </div>
  );
};
